// May this user open this screen? Checked once for every page by Layout, so
// a module URL the user has no right to shows "Erişim Yetkiniz Yok" instead
// of the page skeleton with "+ Yeni ..." buttons, zero KPIs and 403 toasts
// (OFF-16 / X-01 / F33). It mirrors the backend guards:
//
//   /users, /modules/admin/*   company administrators (OFF-14, OFF-15)
//   /modules/<code>/<page>     a right on that page, when the page is one of
//                              config/moduleStructure.js (what Yetki Yönetimi
//                              grants), else a read right somewhere in the
//                              module (require_module_access)
//
// Self-service screens need no module right: the backend answers them for
// the signed-in employee's own records only.
import { MODULE_STRUCTURE } from '../config/moduleStructure';
import { hasPermission, isAdmin } from './permissions';

// /modules/tasks/kanban lists the caller's own tasks (/api/tasks/my): a CRM
// task assigned to a user without Görevler is opened there (S2-06)
export const SELF_SERVICE_PATHS = ['/modules/hr/my-leaves', '/modules/hr/my-payslips', '/modules/tasks/kanban'];
const ADMIN_ONLY_PREFIXES = ['/users', '/modules/admin'];

// A page added after positions were set up is also open to whoever holds the
// right it grew out of (M2-17: the cash flow report came out of the reports
// page; full accounting positions had no cash_flow right, so the summary on
// the Ön Muhasebe home linked to "Erişim Yetkiniz Yok").
// The cash flow is Finans' report and Ön Muhasebe's too: either right opens it.
export const PAGE_FALLBACKS = {
  'accounting.cash_flow': ['accounting.reports', 'finance.cash_flow'],
  'finance.cash_flow': ['accounting.cash_flow', 'accounting.reports'],
  // Finans opens Ön Muhasebe's Banka/Kasa and aging screens
  'accounting.bank_cash': ['finance.bank_accounts'],
  'accounting.aging_analysis': ['finance.collection_tracking'],
};
const fallbacksOf = (key) => [].concat(PAGE_FALLBACKS[key] || []);

const within = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);

/** The moduleStructure pages a /modules path belongs to (longest path wins). */
export const pagesForPath = (moduleCode, pathname) => {
  const pages = MODULE_STRUCTURE[moduleCode]?.pages || {};
  let best = 0;
  let matches = [];
  Object.entries(pages).forEach(([page, { path }]) => {
    if (!path || !within(pathname, path)) return;
    if (path.length > best) {
      best = path.length;
      matches = [page];
    } else if (path.length === best) {
      matches.push(page);
    }
  });
  return matches;
};

export const canOpenPath = (user, pathname = '') => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (isAdmin(user)) return true;
  if (ADMIN_ONLY_PREFIXES.some((p) => within(path, p))) return false;
  if (SELF_SERVICE_PATHS.some((p) => within(path, p))) return true;
  const match = path.match(/^\/modules\/([^/]+)/);
  if (!match) return true;
  const moduleCode = match[1];
  if (!MODULE_STRUCTURE[moduleCode]) return true;
  const pages = pagesForPath(moduleCode, path);
  if (pages.length > 0) {
    return pages.some((page) => {
      const key = `${moduleCode}.${page}`;
      return hasPermission(user, key) || fallbacksOf(key).some((k) => hasPermission(user, k));
    });
  }
  return hasPermission(user, moduleCode, 'read');
};

export default canOpenPath;
